import { Link } from 'react-router-dom';
import { Mail, Globe, ArrowUpRight } from 'lucide-react';

const LOGO_URL = "/logo.jpg";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    navigation: [
      { path: '/', label: 'Home' },
      { path: '/about', label: 'About' },
      { path: '/services', label: 'Services' },
      { path: '/industries', label: 'Industries' },
      { path: '/contact', label: 'Contact' },
    ],
    services: [
      'Strategy & Operations',
      'Finance Advisory',
      'Technology Consulting',
      'Data Readiness',
      'AI/GenAI/LLM & Digital Assets Governance',
    ],
  };

  return (
    <footer className="relative border-t border-border/50" data-testid="footer">
      {/* Gradient top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-6">
              <img 
                src={LOGO_URL} 
                alt="ANJO Consultants" 
                className="h-12 w-auto object-contain rounded-lg"
              />
              <div>
                <span className="text-lg font-bold text-gradient">ANJO</span>
                <span className="text-lg text-muted-foreground ml-1">Consultants</span>
              </div>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Performance, Governance, and Transformation across Private, Public, and Quasi Organizations.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-foreground font-semibold mb-4 text-sm tracking-wider uppercase">Navigation</h4>
            <ul className="space-y-3">
              {footerLinks.navigation.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-muted-foreground hover:text-foreground text-sm transition-colors inline-flex items-center group"
                  >
                    {link.label}
                    <ArrowUpRight size={12} className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-foreground font-semibold mb-4 text-sm tracking-wider uppercase">Services</h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              {footerLinks.services.map((service, index) => (
                <li key={index}>{service}</li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-foreground font-semibold mb-4 text-sm tracking-wider uppercase">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-center space-x-3 text-muted-foreground text-sm group">
                <div className="p-2 rounded-lg bg-secondary group-hover:bg-cyan-500/10 transition-colors">
                  <Mail size={16} className="text-cyan-400" />
                </div>
                <a href="mailto:info@anjolc.com" className="hover:text-foreground transition-colors">
                  info@anjolc.com
                </a>
              </li>
              <li className="flex items-center space-x-3 text-muted-foreground text-sm group">
                <div className="p-2 rounded-lg bg-secondary group-hover:bg-purple-500/10 transition-colors">
                  <Globe size={16} className="text-purple-400" />
                </div>
                <a href="https://www.anjolc.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
                  www.anjolc.com
                </a>
              </li>
            </ul>
            <div className="mt-6">
              <Link
                to="/contact"
                className="inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-lg bg-gradient-to-r from-cyan-500/10 to-purple-500/10 text-foreground border border-cyan-500/30 hover:border-cyan-500/50 hover:from-cyan-500/20 hover:to-purple-500/20 transition-all"
                data-testid="footer-cta"
              >
                Get in Touch
                <ArrowUpRight className="ml-2" size={16} />
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-border/50">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-muted-foreground text-sm">
              © {currentYear} ANJO Consultants. All rights reserved.
            </p>
            <p className="text-muted-foreground/50 text-xs">
              Governance-First Transformation
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
